/**
 * @file components/layout/NotificationBell.tsx
 * @description 通知鈴鐺按鈕元件（Header 右側）。
 *
 * 職責：
 * 1. 每 30 秒輪詢 GET /api/notifications?unread=1 取得未讀數量
 * 2. 以 Badge 顯示未讀數（超過 99 顯示 "99+"）
 * 3. 點擊時呼叫 onClick 回呼（BaseLayout 控制 NotificationDrawer 的 open 狀態）
 *
 * 設計：
 * - 使用 TanStack Query 管理輪詢與快取，Key 為 ['notifications', 'unreadCount']
 * - 不承擔通知列表渲染，列表由 NotificationDrawer 負責
 * - 出現網路錯誤時靜默降級（Badge 歸 0 或保留舊值），不顯示錯誤 toast
 */

import { Badge, Button } from 'antd';
import { Bell } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { color } from '@/styles/tokens';

/** GET /api/notifications 的回應型別（僅用到 unreadCount） */
interface NotificationSummary {
  items: unknown[];
  unreadCount: number;
}

/** 輪詢間隔：30 秒 */
const POLL_INTERVAL_MS = 30_000;

/** 介面 Props */
export interface NotificationBellProps {
  /** 點擊鈴鐺時的回呼（由 BaseLayout 設置，用於開啟 NotificationDrawer） */
  onClick: () => void;
}

/**
 * 通知鈴鐺按鈕。
 *
 * 用法（BaseLayout）：
 * ```tsx
 * <NotificationBell onClick={() => setNotifDrawerOpen(true)} />
 * ```
 */
export function NotificationBell({ onClick }: NotificationBellProps) {
  const { data } = useQuery<NotificationSummary>({
    queryKey: ['notifications', 'unreadCount'],
    queryFn: () => apiClient.get<NotificationSummary>('/api/notifications?unread=1'),
    /** 每 30 秒自動重新拉取 */
    refetchInterval: POLL_INTERVAL_MS,
    /** 頁面重新取得焦點時也重拉 */
    refetchOnWindowFocus: true,
    /** 網路錯誤靜默降級：不拋異常以免 ErrorBoundary 攔截 */
    retry: 1,
  });

  const unreadCount = data?.unreadCount ?? 0;

  return (
    <Badge count={unreadCount} size="small" offset={[-2, 4]} overflowCount={99}>
      <Button
        type="text"
        icon={<Bell size={18} />}
        onClick={onClick}
        style={{
          color: color.text.tertiary,
          width: 36,
          height: 36,
        }}
        aria-label={`通知中心${unreadCount > 0 ? `（${unreadCount} 則未讀）` : ''}`}
      />
    </Badge>
  );
}
